import React from 'react';
import classnames from 'classnames';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { get, map } from 'lodash';
import moment from 'moment';
import { Icon, withStyles, createStyles } from '@material-ui/core';

import { parseDate } from '@lib/services/datesService';
import { capitalizeFirstLetter } from '@lib/services/stringsService';

import { getFirstActiveContact } from 'ui-utils';
import messages from './messages';
import { FormFieldContactsArchived } from '../FormFieldContactsArchived';
import { Typography } from '../Typography';

const styles = ({
  custom: {
    palette: { colorSupportSlate },
    fontSizes: { bodyG, small },
  },
}) =>
  createStyles({
    notes: {
      borderTop: '1px solid #F2F2F2',
      display: 'block',
      marginTop: 15,
      paddingTop: 8,
      paddingLeft: 24,
      width: '100%',
    },
    text: {
      display: 'inline-block',
      height: 30,
      lineHeight: '36px',
      marginRight: 16,
      fontWeight: 'normal',
      paddingRight: 0,
      textTransform: 'initial',
      color: colorSupportSlate,
      fontSize: small,
    },
    icon: {
      fontSize: bodyG,
      marginRight: 8,
      position: 'relative',
      top: 4,
      width: 18,
      color: colorSupportSlate,
    },
    invalid: {
      display: 'inline-block',
    },
  });

class SecondaryTextDetails extends React.PureComponent {
  render() {
    const { details } = this.props;
    if (!details) {
      return <div />;
    }
    return (
      <div style={styles.root}>
        {this.renderFacility()}
        {this.renderAppointmentTypes()}
        {this.renderAttempts()}
        {this.renderAttachments()}
        {this.renderContact()}
        {this.renderAppointmentDate()}
        {this.renderDateOfBirth()}
        {this.renderStatus()}
        {this.renderDateRange()}
        {this.renderInvalid()}
        {this.renderNotes()}
      </div>
    );
  }

  renderFacility() {
    const {
      details: { facility },
    } = this.props;
    const value = get(facility, 'name');

    return this.renderItem(value, 'business');
  }

  renderAppointmentTypes() {
    const {
      details: { appointmentTypes },
    } = this.props;
    const value = map(appointmentTypes, 'name').join(', ');

    return this.renderItem(value, 'content_paste');
  }

  renderAttempts() {
    const {
      details: { attempts },
    } = this.props;

    if (attempts === 'doNotContact') {
      return this.renderItem('Do not contact', 'notifications_off');
    }
    return this.renderItem(attempts, 'call');
  }

  renderAttachments() {
    const {
      details: { attachments },
    } = this.props;

    if (!attachments || attachments <= 0) return;

    return this.renderItem(`${attachments} attachment${attachments > 1 ? 's' : ''}`, 'attach_files');
  }

  renderContact() {
    const { details } = this.props;
    const item = getFirstActiveContact(details);
    const value = get(item, 'value');
    const type = get(item, 'type');
    let icon;

    // TODO unify with `ContactIcon.js` comp
    switch (type) {
      case 'sms':
        icon = 'smartphone';
        break;
      case 'voice':
        icon = 'phone';
        break;
      case 'donotcontact':
        icon = 'notifications_off';
        break;
      case 'whatsapp':
        icon = 'fa fa-whatsapp';
        break;
      default:
        icon = type;
        break;
    }

    return this.renderItem(value, icon);
  }

  renderAppointmentDate() {
    const {
      details: { appointmentDate },
    } = this.props;
    const { formatMessage } = this.props.intl;

    if (!appointmentDate) return;

    const date = moment(appointmentDate);
    const isFuture = moment().diff(date) < 0;
    const formattedDate = date.format('LLL');
    const nextApptLabel = formatMessage(isFuture ? messages.nextAppointmentLabel : messages.lastAppointmentLabel, {
      date: formattedDate,
    });
    const label = `${nextApptLabel}`;

    return this.renderItem(label, 'event_available');
  }

  renderDateOfBirth() {
    const {
      details: { dateOfBirth },
    } = this.props;

    return this.renderItem(parseDate(dateOfBirth), 'cake');
  }

  renderStatus() {
    const {
      details: { status },
    } = this.props;

    if (!status) return;

    let iconKey;

    switch (status) {
      case 'waiting':
        iconKey = 'check_circle';
        break;
      case 'offered':
        iconKey = 'trending_flat';
        break;
      case 'confirming':
        iconKey = 'notifications_active';
        break;
      case 'in-flight':
        iconKey = 'autorenew';
        break;
      default:
        iconKey = 'help';
        break;
    }

    return this.renderItem(capitalizeFirstLetter(status), iconKey);
  }

  renderDateRange() {
    const {
      details: { dateRange },
    } = this.props;

    if (!dateRange) return;

    const { fromDate, toDate } = dateRange;

    if (!fromDate || !toDate) return;

    const label = `${parseDate(fromDate)} - ${parseDate(toDate)}`;

    return this.renderItem(label, 'date_range');
  }

  renderNotes() {
    const {
      details: { notes },
      classes,
    } = this.props;

    if (!notes) return;

    return <div className={classes.notes}>{this.renderItem(notes, 'description')}</div>;
  }

  renderInvalid() {
    const { formatMessage } = this.props.intl;
    const {
      details: { isInvalid },
      classes,
    } = this.props;
    if (isInvalid) {
      return (
        <div className={classes.invalid}>
          <FormFieldContactsArchived label={formatMessage(messages.invalidContact)} variant="bW600F14" />
        </div>
      );
    }
  }

  renderItem(label, iconKey) {
    if (!label) return;

    const { details, classes } = this.props;
    const item = getFirstActiveContact(details);
    const type = get(item, 'type');

    const icon =
      type === 'whatsapp' ? (
        <Icon className={classnames(iconKey, classes.icon)} />
      ) : (
        <Icon className={classes.icon}>{iconKey}</Icon>
      );

    return (
      <Typography className={classes.text} component="div">
        {icon}
        {label}
      </Typography>
    );
  }
}

SecondaryTextDetails.propTypes = {
  details: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
};

export default injectIntl(withStyles(styles)(SecondaryTextDetails));
